import React from 'react'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import { 
  selectSidebarItems, 
  selectSidebarExpandedItems, 
  selectSidebarSelectedCaseId,
  toggleExpand as toggleExpandAction
} from '../../../store/slices/sidebarSlice'
import { 
  FileText, 
  ChevronRight, 
  ChevronDown,
  FileCheck,
  Database,
  Folder,
  FolderOpen,
  AlertCircle,
  CheckCircle2,
  Clock,
  FileSpreadsheet,
  Hash,
  MessageSquare,
  Trash2
} from 'lucide-react'

interface SidebarItem {
  id: string
  label: string
  iconName?: 'Folder' | 'FolderOpen' | 'FileText' | 'FileSpreadsheet' | 'Database' | 'FileCheck' | 'Hash' | 'AlertCircle' | 'MessageSquare' | 'CheckCircle2'
  type: 'case' | 'folder' | 'file' | 'action' | 'validation' | 'extract' | 'summary' | 'flag'
  status?: 'pending' | 'processing' | 'complete' | 'error'
  children?: SidebarItem[]
  metadata?: {
    date?: string
    count?: number
    caseId?: string
    jobId?: string
    documentId?: string
    fullName?: string
    isGlobal?: boolean
  }
}

interface SidebarProps {
  collapsed: boolean
  onToggleCollapse: () => void
  onItemClick: (item: SidebarItem) => void
  onItemDelete?: (item: SidebarItem) => void
  // Highlight any items that have corresponding open tabs
  selectedItemIds?: Set<string>
}


const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  onToggleCollapse,
  onItemClick,
  onItemDelete,
  selectedItemIds
}) => {
  const dispatch = useAppDispatch()
  const sidebarItems = useAppSelector(selectSidebarItems)
  const expandedItemsArr = useAppSelector(selectSidebarExpandedItems)
  const expandedItems = React.useMemo(() => new Set(expandedItemsArr), [expandedItemsArr])
  const selectedCaseId = useAppSelector(selectSidebarSelectedCaseId)
  
  // Responsive width: narrower on smaller screens
  const [width, setWidth] = React.useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 1024 ? 200 : 256 // 12.5rem or 16rem
    }
    return 256
  })
  const [isResizing, setIsResizing] = React.useState(false)
  const minWidth = 180
  const maxWidth = 400

  const handleToggleExpand = (itemId: string) => {
    dispatch(toggleExpandAction(itemId))
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return
      const newWidth = Math.min(maxWidth, Math.max(minWidth, e.clientX))
      setWidth(newWidth)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing])
  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'complete': return 'text-green-500'
      case 'processing': return 'text-blue-500'
      case 'error': return 'text-red-500'
      case 'pending': return 'text-yellow-500'
      default: return 'text-gray-500'
    }
  }

  // Tailwind doesn't pick up dynamic class names, so map indentation explicitly
  const getIndentClass = (level: number) => {
    switch (level) {
      case 0: return ''
      case 1: return 'ml-4'
      case 2: return 'ml-8'
      case 3: return 'ml-12'
      default: return 'ml-12'
    }
  }

  const renderIcon = (name?: SidebarItem['iconName'], className = 'w-4 h-4') => {
    switch (name) {
      case 'Folder': return <Folder className={className} />
      case 'FolderOpen': return <FolderOpen className={className} />
      case 'FileText': return <FileText className={className} />
      case 'FileSpreadsheet': return <FileSpreadsheet className={className} />
      case 'Database': return <Database className={className} />
      case 'FileCheck': return <FileCheck className={className} />
      case 'Hash': return <Hash className={className} />
      case 'AlertCircle': return <AlertCircle className={className} />
      case 'CheckCircle2': return <CheckCircle2 className={className} />
      case 'MessageSquare': return <MessageSquare className={className} />
      default: return null
    }
  }

  const renderSidebarItem = (item: SidebarItem, level = 0) => {
    const hasChildren = item.children && item.children.length > 0
    const isExpanded = expandedItems.has(item.id)
    const isClickable = ['file', 'extract', 'validation', 'summary', 'flag'].includes(item.type)
    const isSelected = !!selectedItemIds?.has(item.id)

    return (
      <div key={item.id}>
        <div
          className={`flex items-center justify-between p-2 hover:bg-gray-100 rounded cursor-pointer group ${getIndentClass(level)} ${item.type === 'case' ? 'font-medium' : ''} ${isSelected ? 'bg-orange-50' : ''}`}
          onClick={() => {
            // Prefer opening content when item is actionable, even if it has children
            if (isClickable) {
              onItemClick(item)
            } else if (hasChildren) {
              handleToggleExpand(item.id)
            }
          }}
        >
          <div className="flex items-center space-x-2 flex-1">
            {hasChildren && (
              <span
                className="w-4 h-4 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation()
                  handleToggleExpand(item.id)
                }}
              >
                {isExpanded ? (
                  <ChevronDown className="w-3 h-3" />
                ) : (
                  <ChevronRight className="w-3 h-3" />
                )}
              </span>
            )}
            {/* For top-level single pages (e.g., Summary/Insights), align icon with chevron by skipping spacer */}
            {!hasChildren && level > 0 && !(item.type === 'summary' && level === 1) && (
              <span className="w-4" />
            )}
            <span className={item.type === 'case' && isExpanded ? 'text-orange-500' : ''}>
              {renderIcon(item.iconName, level === 0 ? 'w-4 h-4' : 'w-3 h-3')}
            </span>
            {!collapsed && (
              <span className={`text-sm truncate max-w-[120px] ${
                item.type === 'case' ? 'text-gray-900' : 'text-gray-700'
              }`} aria-label={item.label}>
                {item.label}
              </span>
            )}
            {!collapsed && item.metadata?.count !== undefined && (
              <span className="text-xs text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded-full">
                {item.metadata.count}
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-1">
            {/* Delete button for documents */}
            {item.type === 'file' && onItemDelete && !collapsed && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  if (window.confirm(`Are you sure you want to delete "${item.label}"?`)) {
                    onItemDelete(item)
                    // Redux state will handle the UI update - no need to reload
                  }
                }}
                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded transition-opacity"
                title="Delete document"
              >
                <Trash2 className="w-3 h-3 text-red-500" />
              </button>
            )}
            
            {!collapsed && item.status && (
              <div className="flex items-center space-x-1">
                {item.status === 'processing' && (
                  <Clock className={`w-3 h-3 ${getStatusColor(item.status)}`} />
                )}
                {item.status === 'complete' && (
                  <CheckCircle2 className={`w-3 h-3 ${getStatusColor(item.status)}`} />
                )}
                {item.status === 'error' && (
                  <AlertCircle className={`w-3 h-3 ${getStatusColor(item.status)}`} />
                )}
                {item.status === 'pending' && (
                  <Clock className={`w-3 h-3 ${getStatusColor(item.status)}`} />
                )}
              </div>
            )}
          </div>
        </div>
        {hasChildren && isExpanded && !collapsed && (
          <div>
            {item.children!.map((child) => renderSidebarItem(child, level + 1))}
          </div>
        )}
      </div>
    )
  }

  // Filter to only the selected case if provided, and get the case name
  const { itemsToRender, selectedCaseName, selectedCaseFullName } = React.useMemo(() => {
    if (!selectedCaseId) {
      return { itemsToRender: sidebarItems, selectedCaseName: null, selectedCaseFullName: null }
    }
    const selectedCase = sidebarItems.find((i: SidebarItem) => i.type === 'case' && i.metadata?.caseId === selectedCaseId)
    const filteredItems = sidebarItems.filter((i: SidebarItem) => i.type !== 'case' || i.metadata?.caseId === selectedCaseId)
    return { 
      itemsToRender: filteredItems, 
      selectedCaseName: selectedCase?.label || null,
      selectedCaseFullName: selectedCase?.metadata?.fullName || selectedCase?.label || null
    }
  }, [selectedCaseId, sidebarItems])

  return (
    <div className="relative flex">
      <div 
        className={`bg-gray-50 border-r border-gray-200 flex flex-col transition-all duration-200 relative`}
        style={{ width: collapsed ? 48 : width }}
      >
        <div className={`${collapsed ? 'p-2' : 'p-3'} border-b border-gray-200`}>
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <h3 
                  className="font-semibold text-gray-900 truncate" 
                  title={selectedCaseFullName || 'Assets Viewer'}
                >
                  {selectedCaseName || 'Assets Viewer'}
                </h3>
              </div>
            )}
            <button
              onClick={onToggleCollapse}
              className="p-1 hover:bg-gray-200 rounded flex-shrink-0"
              title={collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
            >
              {collapsed ? (
                <ChevronRight className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4 rotate-180" />
              )}
            </button>
          </div>
        </div>
        {!collapsed && (
          <div className="flex-1 overflow-y-auto p-2">
            {itemsToRender.map((item: SidebarItem) => renderSidebarItem(item))}
          </div>
        )}
      </div>
      {!collapsed && (
        <div
          className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 hover:opacity-50 transition-opacity"
          onMouseDown={handleMouseDown}
          style={{ 
            backgroundColor: isResizing ? 'rgb(59, 130, 246)' : 'transparent',
            opacity: isResizing ? 0.5 : 1
          }}
        />
      )}
    </div>
  )
}

export default Sidebar
export type { SidebarItem }
